import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeprecatedFAIcon } from '@components-library/Icons/DeprecatedFAIcon';
import { DEPRECATED_FA_GLOBAL_SPRITE } from '@components-library/Icons/interfaces';
import { Tooltip } from '@components-library/Tooltip';
import { TableHeaderRangeFilter } from '@components-shared/table/PaginationTable';
import { getColumnHeaderText } from '@components-shared/table/utils/tableHeaderUtils';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { Popover } from 'antd';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import { TableHeaderFilter } from '../tableHeaderFilter/tableHeaderFilter';
import { TableHeaderCellProps } from '../types';

const TableHeaderCell = observer((props: TableHeaderCellProps): JSX.Element => {
  const { column, getSortedData, headerFilterValues, headerFilterDefaultValues, onFilter, headerSortValues } = props;
  const { accountStore } = useMobxStore();
  const { i18n } = useTranslation();
  const headerTextContext = { utcOffsetInHours: accountStore.generalSettings.utcOffsetInHours };

  const columnTitle = getColumnHeaderText(headerTextContext, column);
  const columnTooltip = column.tooltip ? i18n.t(column.tooltip as KeyMapKeys) : '';
  const isSlashVisible = column.filterType && column.sortable;

  const filterValue = headerFilterValues[column.key] as unknown as TableHeaderRangeFilter;
  const filterDefaultValues = headerFilterDefaultValues[column.key];

  const renderSortButtons = (key: string) => {
    const sortState = headerSortValues.sortingType;
    const columnName = headerSortValues.selectedColumn;

    let isAscending =
      sortState && columnName === key
        ? DEPRECATED_FA_GLOBAL_SPRITE.chevronDownSquare
        : DEPRECATED_FA_GLOBAL_SPRITE.chevronDownSquareSolid;
    let isDescending =
      !sortState && columnName === key
        ? DEPRECATED_FA_GLOBAL_SPRITE.chevronUpSquare
        : DEPRECATED_FA_GLOBAL_SPRITE.chevronUpSquareSolid;

    if (key !== headerSortValues.selectedColumn) {
      isAscending = DEPRECATED_FA_GLOBAL_SPRITE.chevronDownSquare;
      isDescending = DEPRECATED_FA_GLOBAL_SPRITE.chevronUpSquare;
    }

    return (
      <div className="table__icon-wrapper">
        <DeprecatedFAIcon
          onClick={() => getSortedData(column.key, false)}
          className="table__sort-icon"
          icon={isAscending}
        />
        <DeprecatedFAIcon
          onClick={() => getSortedData(column.key, true)}
          className="table__sort-icon"
          icon={isDescending}
        />
      </div>
    );
  };

  const renderFilterButton = () => {
    const icon = Object.values(filterValue).every((i) => i === 0)
      ? DEPRECATED_FA_GLOBAL_SPRITE.filter
      : DEPRECATED_FA_GLOBAL_SPRITE.filterActive;

    return <DeprecatedFAIcon icon={icon} />;
  };

  return (
    <th key={column.key} className={column.className}>
      <div className="table__header_wrapper">
        <div className="table__title_tooltip">
          <span>{columnTitle}</span>
          {columnTooltip && <Tooltip content={columnTooltip} />}
        </div>
        {column.sortable && (
          <div className="table__button-container">
            {renderSortButtons(column.key)}
            {isSlashVisible && <span className="table__slash">/</span>}

            {column.filterType && (
              <Popover
                placement="bottom"
                className="table__filter-popover"
                content={
                  <TableHeaderFilter
                    keyName={column.key}
                    initialState={filterValue}
                    headerFilterDefaultValue={filterDefaultValues}
                    onFilter={onFilter}
                    filterType={column.filterType}
                    limitRange={column.limitRange}
                  />
                }
                trigger="click">
                {renderFilterButton()}
              </Popover>
            )}
          </div>
        )}
      </div>
    </th>
  );
});

export { TableHeaderCell };
